// sales-leads-v1
export const STAGES = [
  { key: "NEW", label: "New", dot: "#0EA5E9" },
  { key: "CONTACTED", label: "Contacted", dot: "#64748B" },
  { key: "QUALIFIED", label: "Qualified", dot: "#6366F1" },
  { key: "SITE_VISIT", label: "Site visit", dot: "#F59E0B" },
  { key: "PROPOSAL_SENT", label: "Proposal sent", dot: "#F97316" },
  { key: "NEGOTIATION", label: "Negotiation", dot: "#A855F7" },
];
export const ALL_STAGES = [...STAGES, { key: "TENDER", label: "Tender", dot: "#64748B" }, { key: "WON", label: "Won", dot: "#16A34A" }, { key: "LOST", label: "Lost", dot: "#DC2626" }];
export const stageLabel = (k) => ALL_STAGES.find((s) => s.key === k)?.label || (k || "").replaceAll("_", " ");
export const boardStage = (k) => (k === "TENDER" ? "QUALIFIED" : k);   // a tender-stage lead sits in the Qualified column
export const PRIORITIES = ["LOW", "MEDIUM", "HIGH"];
export const SOURCES = ["Walk-in", "Website", "Web form", "Referral", "Phone call", "Social media", "Exhibition", "Other"];
export const PROJECT_TYPES = ["Villa", "Residential", "Apartment", "Interior", "Commercial", "Industrial", "Solar / MMS", "Renovation", "Other"];
export const ACTIVITY_KINDS = [
  { key: "CALL", label: "Call" }, { key: "WHATSAPP", label: "WhatsApp" }, { key: "EMAIL", label: "Email" },
  { key: "MEETING", label: "Meeting" }, { key: "SITE_VISIT", label: "Site visit" }, { key: "NOTE", label: "Note" },
];

/** ₹35.0 L, ₹1.85 Cr, ₹12,500 */
export function budget(v) {
  const n = Number(v);
  if (!v || !Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

const DAY = 86400000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
/** Follow-up state: overdue (red) / today (amber) / later. */
export function followup(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const days = Math.round((startOfDay(t) - startOfDay(Date.now())) / DAY);
  if (t < Date.now() && days <= 0) return { tone: days === 0 ? "today" : "overdue", text: days === 0 ? "Today (passed)" : `${-days}d ago` };
  if (days === 0) return { tone: "today", text: "Today" };
  if (days === 1) return { tone: "later", text: "Tomorrow" };
  return { tone: "later", text: `In ${days}d` };
}
export const isOverdue = (iso) => !!iso && new Date(iso).getTime() < Date.now() && startOfDay(iso) < startOfDay(Date.now());
export const isDueToday = (iso) => !!iso && startOfDay(iso) === startOfDay(Date.now());

export const dmy = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "");
export const dmyHm = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }) : "");
export const agoText = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
/** "2026-10-20" + "09:00" in the viewer's time zone -> ISO string for the API */
export const toIso = (date, time) => (date ? new Date(`${date}T${time || "09:00"}`).toISOString() : null);
export const splitIso = (iso) => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso); const p = (n) => String(n).padStart(2, "0");
  return { date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, time: `${p(d.getHours())}:${p(d.getMinutes())}` };
};
export const inDays = (n) => { const d = new Date(Date.now() + n * DAY); const p = (x) => String(x).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };

/** Minimal CSV reader (quotes, commas, newlines inside quotes). */
export function parseCsv(text) {
  const rows = []; let row = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += c; }
    else if (c === '"') q = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += c;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim() !== ""));
}
const HEAD = { name: "title", title: "title", "lead name": "title", phone: "contact_phone", mobile: "contact_phone", email: "contact_email",
  location: "location", city: "location", "project type": "project_type", type: "project_type", budget: "estimated_value",
  source: "source", requirement: "requirement", notes: "notes", priority: "priority" };
/** CSV text -> [{title, contact_phone, ...}] using the header row. */
export function leadsFromCsv(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) return { leads: [], skipped: 0 };
  const cols = rows[0].map((h) => HEAD[h.trim().toLowerCase()] || null);
  let skipped = 0; const leads = [];
  rows.slice(1).forEach((r) => {
    const o = {};
    cols.forEach((k, i) => { const v = (r[i] || "").trim(); if (k && v) o[k] = v; });
    if (!o.title || o.title.length < 2) { skipped++; return; }
    if (o.estimated_value) { const n = Number(String(o.estimated_value).replace(/[₹,\s]/g, "")); if (Number.isFinite(n) && n >= 0) o.estimated_value = n; else delete o.estimated_value; }
    if (o.priority) { o.priority = o.priority.toUpperCase(); if (!PRIORITIES.includes(o.priority)) delete o.priority; }
    if (o.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(o.contact_email)) delete o.contact_email;
    leads.push(o);
  });
  return { leads, skipped };
}
export const CSV_TEMPLATE = "Name,Phone,Email,Location,Project type,Budget,Source,Requirement\nMeera,9876543210,meera@example.com,Nagercoil,Villa,3500000,Website,3BHK villa\n";
